import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, Line } from "react-chartjs-2";
import "chart.js/auto";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, onSnapshot, updateDoc, arrayRemove } from "firebase/firestore";
import { useMediaQuery, Tabs, Tab } from "@mui/material";
import { getStorage, ref, deleteObject } from "firebase/storage";
import "../App.css";

// ✅ Use singletons from our firebase.js
import { auth, db, ensureUserDoc } from "../firebase";

/* ---------- Stripe Add-on Price IDs (SET THESE) ---------- */
const ADDON_PRICE_IDS = {
  extra20:  "price_1SkuoYH9XJICOtRvbyvgyLdw", // ⬅️ replace with your Stripe price ID
  extra100: "price_1SkuowH9XJICOtRv6w4km2TJ", // ⬅️ replace with your Stripe price ID
};

/* ───────────────────────────── helpers ───────────────────────────── */
function msFromAny(v) {
  if (!v) return null;
  if (typeof v === "number") return v > 1e12 ? v : v * 1000; // seconds → ms
  if (v?.toMillis) { try { return v.toMillis(); } catch {} } // Firestore Timestamp
  if (v?._seconds != null) return v._seconds * 1000;         // admin SDK shape
  const d = new Date(v);
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : null;
}
function asDateMaybe(v) {
  const ms = msFromAny(v);
  return ms == null ? null : new Date(ms);
}
function formatDate(d) {
  if (!d) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
function defaultCapFromPackageKey(k) {
  const key = String(k || "").toLowerCase();
  if (key.includes("family_lite")) return 80;
  if (key.includes("family")) return 150;
  if (key.includes("personal")) return 50;
  return 3; // FREE fallback
}
function normalizeBilling(interval) {
  const iv = String(interval || "").toLowerCase();
  if (iv === "year" || iv === "annual" || iv === "yearly") return "annual";
  if (iv === "month" || iv === "monthly") return "monthly";
  return "—";
}
function startOfCalendarMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
}
function endOfCalendarMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 1, 0, 0, 0, 0); // exclusive
}
function parseActivityDate(dateStrOrObj) {
  if (!dateStrOrObj) return null;

  // If it's exactly YYYY-MM-DD, treat it as end-of-day LOCAL time
  if (typeof dateStrOrObj === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateStrOrObj)) {
    const [y, m, d] = dateStrOrObj.split("-").map(Number);
    return new Date(y, m - 1, d, 23, 59, 59, 999);
  }

  // Otherwise fall back to existing logic
  return typeof dateStrOrObj === "string"
    ? new Date(dateStrOrObj)
    : asDateMaybe(dateStrOrObj);
}

function inRange(dateStrOrObj, start, endExclusive) {
  const d = parseActivityDate(dateStrOrObj);
  if (!d) return false;
  if (start && d < start) return false;
  if (endExclusive && d >= endExclusive) return false;
  return true;
}


/** Extract billing period from user doc (Stripe-based). Fallback to calendar month if missing. */
function getBillingWindow(u) {
  const sub = u?.subscription || null;
  const start = asDateMaybe(sub?.currentPeriodStart);
  const end = asDateMaybe(sub?.currentPeriodEnd);
  if (start && end) return { start, end };

  // fallback: calendar month
  const now = new Date();
  return { start: startOfCalendarMonth(now), end: endOfCalendarMonth(now) };
}

/** Compute the CAP (included stories) from entitlements/legacy/packageKey */
function resolveCap(u) {
  const sub = u?.subscription || null;
  const ent = u?.entitlements || {};
  const legacyPlan = typeof u?.plan === "object" ? u.plan : null;

  let cap =
    Number(ent?.storyQuotaPerMonth ?? (legacyPlan?.capStories != null ? legacyPlan.capStories : 0)) || 0;
  if (!cap && sub?.packageKey) cap = defaultCapFromPackageKey(sub.packageKey);
  return cap;
}

/** Add-ons remaining (non-expired) */
function remainingAddons(u) {
  const nowMs = Date.now();
  const addonsRaw = Array.isArray(u?.addons) ? u.addons : [];
  const remaining = addonsRaw
    .filter((a) => {
      const expMs = msFromAny(a?.expiresAt);
      // keep if no expiry, or expiry is in the future
      return expMs == null || expMs > nowMs;
    })
    .reduce((sum, a) => sum + Number(a?.remaining || 0), 0);
  return remaining;
}

/** Sum personal user's stories generated in a window (from readingActivity array) */
function sumStoriesInWindowPersonal(userDoc, start, endExclusive) {
  const arr = Array.isArray(userDoc?.readingActivity) ? userDoc.readingActivity : [];
  let total = 0;

  for (const e of arr) {
    const when = e?.date || e?.ts || e?.time || null;
    const count = Number(e?.storiesGenerated || 0);

    if (!start && !endExclusive) {
      // All-time: just sum valid numeric counts
      total += Number.isFinite(count) ? count : 0;
    } else if (inRange(when, start, endExclusive)) {
      // Windowed: only sum entries inside the range
      total += Number.isFinite(count) ? count : 0;
    }
  }

  return total;
}


/* ✅ NEW: sanitize a genre map and pick the top genre (by numeric count). */
function pickTopGenreName(userDoc, rangeStart, rangeEnd) {
  // Use favoriteGenres / genresExplored, but ignore any broken entries like {"0":"Comedy"}
  const fgRaw = { ...(userDoc?.favoriteGenres || {}) };
  const geRaw = { ...(userDoc?.genresExplored || {}) };

  // Only show a genre if there's activity in the chosen range (same behavior you wanted)
  const activity = Array.isArray(userDoc?.readingActivity) ? userDoc.readingActivity : [];
  const hasRangeData =
    !rangeStart && !rangeEnd
      ? true
      : activity.some((e) => inRange(e?.date || e?.ts || null, rangeStart, rangeEnd));
  if (!hasRangeData) return "—";

  // Merge maps
  const merged = new Map();
  const add = (obj) => {
    Object.entries(obj).forEach(([k, v]) => {
      // Accept only string keys that look like genres and numeric counts
      if (typeof k === "string" && k !== "0" && typeof v === "number" && v > 0) {
        merged.set(k, (merged.get(k) || 0) + v);
      }
    });
  };
  add(fgRaw);
  add(geRaw);

  if (merged.size === 0) return "—";

  // Pick the max
  let best = "—";
  let bestVal = -Infinity;
  merged.forEach((val, key) => {
    if (val > bestVal) {
      bestVal = val;
      best = key;
    }
  });
  return best;
}

/* ✅ NEW: keep only stable, meaningful fields for comparing subscription before/after portal */
function normalizeSub(sub) {
  const s = sub || {};
  return {
    displayName: s.displayName || null,
    packageKey: s.packageKey || null,
    billingInterval: s.billingInterval || null,
    status: s.status || null,
    currentPeriodStart: msFromAny(s.currentPeriodStart) || null,
    currentPeriodEnd: msFromAny(s.currentPeriodEnd) || null,
    priceId: s.priceId || null,
    seats: typeof s.seats === "number" ? s.seats : null,
  };
}

/* ───────────────────────────── component ───────────────────────────── */

const PersonalDashboard = ({ overrideUid = null }) => {

  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [userDoc, setUserDoc] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [diversityMode, setDiversityMode] = useState(
    () => localStorage.getItem("diversityMode") === "true"
  );
  const [buying, setBuying] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);

  // Range toggle: 'billing' | 'month' | 'all'
  const [rangeMode, setRangeMode] = useState(() => localStorage.getItem("rangeMode") || "billing");

 // Live user doc + email verification override
useEffect(() => {
  // 🔹 Admin "view as": subscribe directly to the target user's doc
  if (overrideUid) {
    const ref = doc(db, "users", overrideUid);
    const unsubDoc = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        setUserDoc(data);
        // In admin view we don't care about email verification for gating
      },
      (err) => console.error("user snapshot error (admin view):", err)
    );
    return () => unsubDoc();
  }

  // 🔹 Normal behaviour for a logged-in personal user
  const unsubAuth = onAuthStateChanged(auth, (user) => {
    if (!user) {
      setUserDoc(null);
      return;
    }

    ensureUserDoc().catch(() => {});
    const ref = doc(db, "users", user.uid);

    const unsubDoc = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        setUserDoc(data);


      },
      (err) => console.error("user snapshot error:", err)
    );

    return () => unsubDoc();
  });

  return () => unsubAuth();
}, [overrideUid]);




  /* ✅ NEW: after returning from portal, decide whether to show success (only if changed) */
  useEffect(() => {
    if (!userDoc) return;
    const beforeStr = localStorage.getItem("ysw.sub.snapshot");
    if (!beforeStr) return; // not coming back from portal

    try {
      const before = JSON.parse(beforeStr);
      const after = normalizeSub(userDoc?.subscription || {});
      const changed = JSON.stringify(before) !== JSON.stringify(after);

      // cleanup flags either way
      localStorage.removeItem("ysw.sub.snapshot");
      localStorage.removeItem("ysw.portal.startedAt");

      if (changed) {
        navigate("/billing-success", { replace: true });
      }
    } catch {
      localStorage.removeItem("ysw.sub.snapshot");
      localStorage.removeItem("ysw.portal.startedAt");
      // If parsing fails, silently continue and remain on dashboard
    }
  }, [userDoc, navigate]);

  useEffect(() => {
    const onStorage = () => {
      setDiversityMode(localStorage.getItem("diversityMode") === "true");
      const rm = localStorage.getItem("rangeMode");
      if (rm) setRangeMode(rm);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  /* ----- Derived usage (billing window only for sidebar) ----- */
  const billingWindow = useMemo(() => getBillingWindow(userDoc || {}), [userDoc]);
  const calendarWindow = useMemo(() => {
    const now = new Date();
    return { start: startOfCalendarMonth(now), end: endOfCalendarMonth(now) };
  }, []);

  function getCurrentWindow() {
    if (rangeMode === "billing") return billingWindow;
    if (rangeMode === "month") return calendarWindow;
    return { start: null, end: null }; // all time
  }

  const sidebarUsage = useMemo(() => {
  const cap = resolveCap(userDoc || {});

  const sub = userDoc?.subscription || null;
  const planLegacyObj = typeof userDoc?.plan === "object" ? userDoc.plan : null;
  const planString = typeof userDoc?.plan === "string" ? userDoc.plan : null;

  const billingNorm =
    normalizeBilling(sub?.billingInterval) || normalizeBilling(planLegacyObj?.billing) || "—";

  const { start: billStart, end: billEnd } = billingWindow;
  const { start: monthStart, end: monthEnd } = calendarWindow;

  // 👇 Annual plans: show monthly view in sidebar; Monthly plans: billing window
  const isAnnual = billingNorm === "annual";
  const windowStart = isAnnual ? monthStart : billStart;
  const windowEnd = isAnnual ? monthEnd : billEnd;

  const used = sumStoriesInWindowPersonal(userDoc || {}, windowStart, windowEnd);
  const addonsRem = remainingAddons(userDoc || {});
  const remainingIncluded = Math.max(cap - used, 0);
  const remainingTotal = remainingIncluded + addonsRem;

  const planLabel = sub?.displayName || planString || planLegacyObj?.id || "Free";
  const periodEnd =
    asDateMaybe(sub?.currentPeriodEnd) || asDateMaybe(planLegacyObj?.currentPeriodEnd) || null;

  // combined (segmented) bar
  const usedIncluded = Math.min(used, cap);
  const usedFromAddons = Math.max(used - cap, 0);
  const totalPool = cap + addonsRem + usedFromAddons; // cap + remaining add-ons + any add-on already used
  const usedTotal = usedIncluded + usedFromAddons;
  const pctIncluded = totalPool ? (usedIncluded / totalPool) * 100 : 0;
  const pctAddon = totalPool ? (usedFromAddons / totalPool) * 100 : 0;

  return {
    cap,
    used,
    remainingIncluded,
    remainingAddons: addonsRem,
    remainingTotal,
    planLabel,
    billing: billingNorm,
    periodEnd,
    usedIncluded,
    usedFromAddons,
    totalPool,
    usedTotal,
    pctIncluded,
    pctAddon,
    isAnnual, // 👈 NEW: so the JSX can adjust wording
  };
}, [userDoc, billingWindow, calendarWindow]);


  /* ----- Reading Summary (respects rangeMode) ----- */
  const { start: rangeStart, end: rangeEnd } = getCurrentWindow();

  const storiesGeneratedThisRange = useMemo(() => {
    return sumStoriesInWindowPersonal(userDoc || {}, rangeStart, rangeEnd);
  }, [userDoc, rangeStart, rangeEnd]);

  const totalReadingTimeThisRange = useMemo(() => {
    const arr = Array.isArray(userDoc?.readingActivity) ? userDoc.readingActivity : [];
    return arr.reduce((sum, e) => {
      const when = e?.date || e?.ts || null;
      if (!rangeStart && !rangeEnd) return sum + Number(e?.timeSpent || 0);
      return inRange(when, rangeStart, rangeEnd) ? sum + Number(e?.timeSpent || 0) : sum;
    }, 0);
  }, [userDoc, rangeStart, rangeEnd]);

  const averageReadingTimeThisRange = useMemo(() => {
    const stories = storiesGeneratedThisRange || 0;
    return stories > 0 ? Math.round(totalReadingTimeThisRange / stories / 60) : 0;
  }, [storiesGeneratedThisRange, totalReadingTimeThisRange]);

  /* ✅ NEW: robust, name-only most-popular genre */
  const mostPopularGenre = useMemo(() => {
    return pickTopGenreName(userDoc || {}, rangeStart, rangeEnd);
  }, [userDoc, rangeStart, rangeEnd]);

  /* ----- Charts (fixed time windows) ----- */
  const last7Days = useMemo(() => {
    const arr = Array.from({ length: 7 }, (_, index) => {
      const d = new Date();
      d.setDate(d.getDate() - index);
      return d.toISOString().split("T")[0];
    }).reverse();
    return arr;
  }, []);

  const last6Weeks = useMemo(() => {
    const arr = Array.from({ length: 6 }, (_, index) => {
      const today = new Date();
      const start = new Date(today);
      start.setDate(today.getDate() - today.getDay() - index * 7);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return {
        label: `${start.getDate()} ${start.toLocaleString("default", {
          month: "short",
        })} - ${end.getDate()} ${end.toLocaleString("default", {
          month: "short",
        })}`,
        start,
        end,
      };
    }).reverse();
    return arr;
  }, []);

  const storiesReadData = useMemo(
    () => ({
      labels: last7Days,
      datasets: [
        {
          label: "Stories Read Per Day",
          data: last7Days.map((d) => {
            const e = (userDoc?.readingActivity || []).find((x) => x.date === d);
            return e ? e.storiesGenerated || 0 : 0;
          }),
          backgroundColor: "rgba(0, 123, 255, 0.5)",
          borderColor: "rgba(0, 123, 255, 1)",
          borderWidth: 2,
        },
      ],
    }),
    [last7Days, userDoc]
  );

  const timeSpentReadingData = useMemo(
    () => ({
      labels: last7Days,
      datasets: [
        {
          label: "Total Reading Time Per Day (Minutes)",
          data: last7Days.map((d) => {
            const e = (userDoc?.readingActivity || []).find((x) => x.date === d);
            return e ? Math.round((e.timeSpent || 0) / 60) : 0;
          }),
          backgroundColor: "rgba(255, 99, 132, 0.5)",
          borderColor: "rgba(255, 99, 132, 1)",
          borderWidth: 2,
        },
      ],
    }),
    [last7Days, userDoc]
  );

  const readingStreaksData = useMemo(
    () => ({
      labels: last6Weeks.map((w) => w.label),
      datasets: [
        {
          label: "Reading Streak (Days per Week)",
          data: last6Weeks.map((w) =>
            (userDoc?.readingActivity || []).filter((e) => {
              const d = new Date(e.date);
              return d >= w.start && d <= w.end;
            }).length
          ),
          backgroundColor: "rgba(255, 165, 0, 0.6)",
          borderColor: "rgba(255, 165, 0, 1)",
          borderWidth: 2,
        },
      ],
    }),
    [last6Weeks, userDoc]
  );

  const favoriteGenresData = useMemo(
    () => ({
      labels: Object.keys(userDoc?.favoriteGenres || {}),
      datasets: [
        {
          label: userDoc?.fullName || (userDoc?.email || "").split("@")[0] || "You",
          data: Object.values(userDoc?.favoriteGenres || {}),
          backgroundColor: "rgba(0, 123, 255, 0.6)",
          borderColor: "rgba(0, 123, 255, 1)",
          borderWidth: 2,
        },
      ],
    }),
    [userDoc]
  );

  const averageReadingTimeData = useMemo(
    () => ({
      labels: last7Days,
      datasets: [
        {
          label: "Average Reading Time Per Session (Minutes)",
          data: last7Days.map((d) => {
            const e = (userDoc?.readingActivity || []).find((x) => x.date === d);
            if (e && e.storiesGenerated > 0) {
              return Math.round((e.timeSpent || 0) / e.storiesGenerated / 60);
            }
            return 0;
          }),
          backgroundColor: "rgba(0, 128, 0, 0.5)",
          borderColor: "rgba(0, 128, 0, 1)",
          borderWidth: 2,
        },
      ],
    }),
    [last7Days, userDoc]
  );

  const comprehensionScoresData = useMemo(
    () => ({
      labels: (userDoc?.comprehensionScores || []).map((e) => {
        const d = new Date(e.date);
        return `${d.getDate()} ${d.toLocaleString("default", { month: "short" })}`;
      }),
      datasets: [
        {
          label: "Comprehension Score (%)",
          data: (userDoc?.comprehensionScores || []).map((e) => e.score || 0),
          backgroundColor: "rgba(153, 102, 255, 0.5)",
          borderColor: "rgba(153, 102, 255, 1)",
          borderWidth: 2,
          fill: false,
        },
      ],
    }),
    [userDoc]
  );




const endMs = sidebarUsage.periodEnd ? sidebarUsage.periodEnd.getTime() : null;

// ✅ Expired should flip immediately after period end (no rounding delays)
const isExpired = endMs != null && Date.now() > endMs;

// Days remaining: only for “renewal soon” messaging
const expDays =
  endMs != null ? Math.ceil((endMs - Date.now()) / 86400000) : null;

// 🛟 Belt & braces: billing window must exist
const hasPeriodEnd = !!sidebarUsage.periodEnd;

// 🟡 Only “renewal soon” if still in the future
const nearExpiry = expDays != null && !isExpired && expDays <= 5;




  /** Start Story (navigate; consumption happens in StoryApp) */
const handleStartStoryFlow = () => {
const user = auth.currentUser;
if (!user) {
  alert("Please sign in again.");
  return;
}

  if (isExpired) {
    alert(
      "Your subscription has expired. Please renew your plan in Billing to keep creating stories."
    );
    return;
  }

  if (sidebarUsage.remainingTotal <= 0) {
    alert("You’ve run out of stories for this billing period. Buy an add-on to keep going!");
    return;
  }

  // Remember where we came from
  sessionStorage.setItem("ysw_last_dashboard", "/personal-dashboard");
  navigate(
    `/storyappuser?profile=${encodeURIComponent(
      userDoc?.fullName || userDoc?.email || "Reader"
    )}&diversity=${diversityMode}&source=personal&from=/personal-dashboard`,
    {
      state: {
        source: "personal",
        from: "/personal-dashboard",
        profile: userDoc?.fullName || userDoc?.email || "Reader",
      },
    }
  );
};


  /** Add-on checkout (HTTP with Bearer; same as Family) */
  const startAddonCheckout = async (addonKey) => {
    const priceId =
      addonKey === "extra20" ? ADDON_PRICE_IDS.extra20 :
      addonKey === "extra100" ? ADDON_PRICE_IDS.extra100 : "";

    if (!priceId) {
      alert("Add-ons checkout isn’t wired yet. Add your Stripe price IDs in ADDON_PRICE_IDS.");
      return;
    }
    const user = auth.currentUser;
    if (!user) {
      alert("Please sign in again.");
      return;
    }

    setBuying(true);
    try {
      await user.getIdToken(true);
      const idToken = await user.getIdToken();
      const resp = await fetch(
        "https://createaddoncheckoutsessionhttp-szcrkw6k7q-uc.a.run.app",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            priceId,
            quantity: 1,
            successUrl: window.location.origin + "/personal-dashboard?topup=1",
            cancelUrl:  window.location.origin + "/personal-dashboard?cancelled=1",
            metadata: { addonKey },
          }),
        }
      );
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json?.error?.message || `Checkout failed (${resp.status}).`);
      if (!json?.url) throw new Error("No checkout URL returned.");
      window.location.href = json.url;
    } catch (e) {
      console.error("Add-on checkout error:", e);
      alert(e?.message || "Could not start checkout.");
    } finally {
      setBuying(false);
    }
  };


const handleRenewSubscription = async () => {
  const user = auth.currentUser;
  if (!user) {
    alert("Please sign in again.");
    return;
  }

  // Try to pick the existing plan’s Stripe priceId
  const priceId =
    userDoc?.subscription?.stripe?.priceId ||
    userDoc?.subscription?.stripe?.price_id ||
    userDoc?.subscription?.priceId ||
    null;

  if (!priceId) {
    alert(
      "We couldn’t detect your current plan in Stripe.\n\n" +
      "Please use Manage Billing in the meantime or contact support."
    );
    return;
  }

  try {
    setOpeningPortal(true);

    // So BillingSuccessRedirect knows to send you back here
    localStorage.setItem("lastDashboard", "/personal-dashboard");

    const idToken = await user.getIdToken(true);
    const resp = await fetch(
      "https://us-central1-yourstoryworld-df05d.cloudfunctions.net/createSubscriptionCheckoutSessionHttp",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          priceId,
          quantity: 1,
          // On success, Stripe will redirect to /billing-success?session_id=...
          successUrl: window.location.origin + "/billing-success",
          // If they cancel, just land back on this dashboard
          cancelUrl: window.location.origin + "/personal-dashboard",
          metadata: {
            renewal: "1",
            source: "personal-dashboard",
          },
        }),
      }
    );

    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      throw new Error(json?.error?.message || `Checkout failed (${resp.status}).`);
    }
    if (!json?.url) throw new Error("No checkout URL returned.");
    window.location.href = json.url;
  } catch (err) {
    console.error("Renewal checkout error:", err);
    alert(
      err?.message ||
        "Could not start subscription renewal. You can also try Manage Billing."
    );
  } finally {
    setOpeningPortal(false);
  }
};


  /** Billing portal (HTTP with Bearer; same as Family) */
  const openBillingPortal = async () => {
    const user = auth.currentUser;
    if (!user) {
      alert("Please sign in again to manage billing.");
      return;
    }
    setOpeningPortal(true);
    try {

       // So BillingSuccessRedirect knows where to send you back
    localStorage.setItem("lastDashboard", "/personal-dashboard");
      /* ✅ NEW: stash a 'before' subscription snapshot locally */
      const before = normalizeSub(userDoc?.subscription || {});
      localStorage.setItem("ysw.sub.snapshot", JSON.stringify(before));
      localStorage.setItem("ysw.portal.startedAt", String(Date.now()));

      await user.getIdToken(true);
      const t = await user.getIdToken();
      const r = await fetch(
        "https://us-central1-yourstoryworld-df05d.cloudfunctions.net/createPortalSessionHttp",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}` },
          body: JSON.stringify({ returnUrl: window.location.origin + "/personal-dashboard" }),
        }
      );
      const j = await r.json();
      if (!r.ok) throw new Error(j?.error?.message || `Portal failed: ${r.status}`);
      if (j?.url) window.location.href = j.url;
      else throw new Error("No portal URL returned.");
    } catch (e) {
      console.error(e);
      alert("Could not open billing portal. Please try again in a moment.");
    } finally {
      setOpeningPortal(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (e) {
      console.error("Logout error:", e);
    }
  };

  const userLoading = userDoc === null;
  if (userLoading) return <p style={{ padding: 16 }}>Loading...</p>;

  const nearEmpty = sidebarUsage.remainingTotal <= 5;



  return (
    <div
      className="full-screen-wrapper"
      style={{
        minHeight: "100vh",
        width: "100%",
        overflowY: "auto",
        overflowX: "auto",
        backgroundImage: `url(${require("../assets/images/landing_background.jpg")})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center center",
        backgroundAttachment: isMobile ? "scroll" : "fixed",
        display: "flex",
        flexDirection: "column",
        fontFamily: "Montserrat, sans-serif",
      }}
    >
      {/* Header */}
      <header className="page-header">
        <div className="logo">
          <img
            src={require("../assets/images/logo_name.PNG")}
            alt="YourStoryWorld Logo"
            style={{ height: "50px", cursor: "pointer" }}
            onClick={() => navigate("/")}
          />
        </div>
        <nav style={{ display: "flex", gap: "15px", justifyContent: "flex-end" }}>
          <button onClick={() => navigate("/help")} className="header-button">Help</button>
          <button onClick={() => navigate("/")} className="header-button">Home</button>
          <button
            onClick={handleLogout}
            className="header-button"
            style={{ backgroundColor: "blue", color: "white", fontWeight: "bold" }}
          >
            Logout
          </button>
        </nav>
      </header>

      {/* Main */}
      <div
        className="dashboard-flex"
        style={{ display: "flex", flexDirection: "row", padding: "20px", gap: "20px", alignItems: "flex-start", flexWrap: "wrap" }}
      >
        {/* Sidebar */}
        <aside
          className="sidebar-section"
          style={{
            width: isMobile ? "100%" : 280,
            background: "rgba(230, 245, 255, 0.95)",
            padding: 20,
            borderRadius: 10,
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div>
            <h2 style={{ fontFamily: "Montserrat, sans-serif", color: "#007BFF", marginBottom: 6 }}>
              {userDoc?.fullName || userDoc?.email || "User"}
            </h2>

            <div style={{ fontWeight: 700, marginBottom: 8 }}>
              Plan: {sidebarUsage.planLabel} {sidebarUsage.billing !== "—" ? `(${sidebarUsage.billing})` : ""}
            </div>

            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
  Your plan renews automatically each {sidebarUsage.billing === "annual" ? "year" : "month"} unless you cancel in billing.
</div>


            {/* Combined usage line */}
            <div style={{ fontSize: 12, margin: "8px 0 4px" }}>
  Stories this{" "}
  {sidebarUsage.isAnnual ? "month (from your annual plan)" : "billing period"}
  : <strong> {sidebarUsage.usedTotal}</strong> / {sidebarUsage.totalPool}
</div>



            {/* Combined segmented progress bar */}
            <div
              aria-label="total usage"
              style={{
                height: 10,
                background: "#e5e7eb",
                borderRadius: 999,
                overflow: "hidden",
                display: "flex",
              }}
              title={`Included used: ${sidebarUsage.usedIncluded}, Add-on used: ${sidebarUsage.usedFromAddons}`}
            >
              <div style={{ width: `${sidebarUsage.pctIncluded}%`, background: "#3b82f6" }} />
              <div style={{ width: `${sidebarUsage.pctAddon}%`, background: "#10b981" }} />
            </div>

            {/* Legend */}
            <div style={{ fontSize: 11, marginTop: 6, display: "flex", gap: 12, color: "#334155" }}>
              <span>
                <span style={{ display: "inline-block", width: 10, height: 10, background: "#3b82f6", marginRight: 6 }} />
                Included
              </span>
              <span>
                <span style={{ display: "inline-block", width: 10, height: 10, background: "#10b981", marginRight: 6 }} />
                Add-ons used
              </span>
            </div>

            {/* Balances */}
            <div style={{ fontSize: 12, marginTop: 8 }}>
              Add-ons remaining: <strong>{sidebarUsage.remainingAddons}</strong>
            </div>
            <div style={{ fontSize: 12 }}>
              Total remaining: <strong>{sidebarUsage.remainingTotal}</strong>
            </div>

            <div style={{ fontSize: 12, marginTop: 10 }}>
              Renews: <strong>{formatDate(sidebarUsage.periodEnd)}</strong>
              {nearExpiry && (
                <div style={{ color: "#b45309", marginTop: 4 }}>
                  ⏳ Renewal soon ({expDays} day{expDays === 1 ? "" : "s"})
                </div>
              )}
            </div>
          </div>

          {nearEmpty && (
            <div
              role="alert"
              style={{
                background: "#fff7ed",
                border: "1px solid #fed7aa",
                color: "#9a3412",
                padding: 10,
                borderRadius: 8,
                fontSize: 13,
              }}
            >
              You’re almost out of stories. Consider an add-on to keep going.
            </div>
          )}

          {isExpired && (
  <div
    style={{
      marginTop: 10,
      fontSize: 13,
      color: "#b91c1c",
      background: "#fee2e2",
      border: "1px solid #fecaca",
      padding: "8px 10px",
      borderRadius: 6,
    }}
  >
    <strong>Your subscription has expired.</strong>
    <div style={{ marginTop: 4 }}>
      Please renew or update your plan in billing to keep creating stories.
    </div>
  </div>
)}


          {/* Top-up buttons + helper text */}
<div
  style={{
    display: "flex",
    gap: 8,
    marginTop: 6,
    flexWrap: "wrap",
  }}
>
  <button
    className="subscription-button"
    onClick={() => startAddonCheckout("extra20")}
    disabled={buying}
    title="Buy Extra 20 stories"
  >
    {buying ? "Processing…" : "+ Extra 20"}
  </button>

  <button
    className="subscription-button"
    onClick={() => startAddonCheckout("extra100")}
    disabled={buying}
    title="Buy Extra 100 stories"
  >
    {buying ? "Processing…" : "+ Extra 100"}
  </button>
</div>

<p style={{ fontSize: 12, color: "#334155", marginTop: 6 }}>
  Need more stories this month? Top up your balance instantly. You’ll be redirected to a
  secure Stripe checkout, and your extra stories will be available right away.
</p>

{/* Renewal + billing actions */}
{isExpired && (
  <button
    className="subscription-button"
    onClick={handleRenewSubscription}
    style={{ marginTop: 8, opacity: openingPortal ? 0.8 : 1 }}
    title="Renew this same plan so you can keep creating stories."
    disabled={openingPortal}
  >
    {openingPortal ? "Starting renewal…" : "Renew this plan"}
  </button>
)}

<button
  className="subscription-button"
  onClick={openBillingPortal}
  style={{ marginTop: 8, opacity: openingPortal ? 0.8 : 1 }}
  title="Change plan, update payment method, or view invoices in Stripe."
  disabled={openingPortal}
>
  {openingPortal ? "Opening billing…" : "Change or Cancel plan / Manage Billing"}
</button>


          <div style={{ marginTop: 12, background: "#f4f4f4", padding: 10, borderRadius: 8 }}>
            <label style={{ display: "flex", alignItems: "center", cursor: "pointer", gap: 10, fontSize: 14 }}>
              <input
                type="checkbox"
                checked={diversityMode}
                onChange={() => {
                  const v = !diversityMode;
                  setDiversityMode(v);
                  localStorage.setItem("diversityMode", String(v));
                }}
              />
              <span>Enable Diversity Mode</span>
            </label>
            <p style={{ fontSize: 12, color: "#555", marginTop: 6 }}>
              Make characters, settings and themes reflect diverse cultures and backgrounds.
            </p>
          </div>
        </aside>

        {/* Main content */}
        <div style={{ flex: 1 }}>
          <h1 style={{ fontFamily: "Montserrat, sans-serif", color: "#007BFF", textAlign: "center" }}>
            Welcome to Your Dashboard
          </h1>

          {/* Start Story (no consume here) */}
          <div
            style={{
              background: "rgba(255, 255, 255, 0.9)",
              padding: 30,
              borderRadius: 10,
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              textAlign: "center",
              marginBottom: 20,
            }}
          >
            <h2>Click the button below to start creating your stories!</h2>
            <button
  onClick={handleStartStoryFlow}
  disabled={
  sidebarUsage.remainingTotal <= 0 ||
  isExpired ||
  !hasPeriodEnd
}

  style={{
    padding: "15px 30px",
    backgroundColor:
      sidebarUsage.remainingTotal <= 0 || isExpired ||
  !hasPeriodEnd
        ? "#9ca3af"
        : "#007BFF",
    color: "white",
    border: "none",
    borderRadius: 5,
    fontSize: "1rem",
    cursor:
      sidebarUsage.remainingTotal <= 0 || isExpired ||
  !hasPeriodEnd
        ? "not-allowed"
        : "pointer",
    marginTop: 10,
  }}
 title={
  !hasPeriodEnd
    ? "Billing information is still loading. Please refresh in a moment."
    : isExpired
    ? "Your subscription has expired. Renew to keep generating stories."
    : sidebarUsage.remainingTotal <= 0
    ? "No stories left this month. Buy an add-on."
    : "Create a Story"
}
>
  {isExpired
  ? "Subscription Expired"
  : sidebarUsage.remainingTotal <= 0
  ? "Limit Reached"
  : "Create a Story"}
</button>



            {sidebarUsage.remainingTotal <= 0 && (
              <div style={{ marginTop: 10, fontSize: 13, color: "#b91c1c" }}>
                You’re out of stories. Buy an add-on to continue.
              </div>
            )}
          </div>

          {/* Reading Summary (respects rangeMode; no extra note needed here) */}
          <div
            className="reading-summary"
            style={{
              background: "linear-gradient(to right, #2C3E50, #4CA1AF)",
              padding: 20,
              borderRadius: 10,
              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
              textAlign: "center",
              marginBottom: 20,
              color: "white",
            }}
          >
            {/* Range buttons */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ display: "inline-flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  className={`subscription-button ${rangeMode === "billing" ? "active" : ""}`}
                  onClick={() => { setRangeMode("billing"); localStorage.setItem("rangeMode", "billing"); }}
                  title="Show stats for your current subscription billing period."
                  style={{ background: rangeMode === "billing" ? "#0ea5e9" : undefined }}
                >
                  Billing Period
                </button>
                <button
                  className={`subscription-button ${rangeMode === "month" ? "active" : ""}`}
                  onClick={() => { setRangeMode("month"); localStorage.setItem("rangeMode", "month"); }}
                  title="Show stats since the 1st of this calendar month."
                  style={{ background: rangeMode === "month" ? "#0ea5e9" : undefined }}
                >
                  This Month
                </button>
                <button
                  className={`subscription-button ${rangeMode === "all" ? "active" : ""}`}
                  onClick={() => { setRangeMode("all"); localStorage.setItem("rangeMode", "all"); }}
                  title="Show all-time totals."
                  style={{ background: rangeMode === "all" ? "#0ea5e9" : undefined }}
                >
                  All Time
                </button>
              </div>
              <div style={{ fontSize: 12, opacity: 0.9, marginTop: 8 }}>
  Charts & summary follow your selection. The <strong>sidebar</strong> shows{" "}
  {sidebarUsage.isAnnual
    ? "your monthly allowance from your annual plan (included cap + add-ons)."
    : "your billing-period balance (included cap + add-ons)."}
</div>

            </div>

            <h2>📊 Reading Summary</h2>
            <p style={{ fontSize: 14, opacity: 0.9 }}>
              Currently showing:{" "}
              <strong>
                {rangeMode === "billing" ? "Billing Period" : rangeMode === "month" ? "This Month" : "All Time"}
              </strong>
            </p>
            <div
              style={{ display: "flex", justifyContent: "space-around", marginTop: 10, flexWrap: "wrap", gap: 16 }}
            >
              <div>
                <h3 style={{ color: "#F9C74F", fontSize: "1.8rem" }}>📖 {storiesGeneratedThisRange}</h3>
                <p>Stories Generated</p>
              </div>
              <div>
                <h3 style={{ color: "#F94144", fontSize: "1.8rem" }}>
                  ⏳ {Math.round(totalReadingTimeThisRange / 60)} min
                </h3>
                <p>Total Reading Time</p>
              </div>
              <div>
                <h3 style={{ color: "#90BE6D", fontSize: "1.8rem" }}>
                  📊 {averageReadingTimeThisRange} min
                </h3>
                <p>Avg. Reading Time</p>
              </div>
              <div>
                <h3 style={{ color: "#F3722C", fontSize: "1.8rem" }}>🌟 {mostPopularGenre}</h3>
                <p>Most Popular Genre</p>
              </div>
            </div>
          </div>

          {/* Tabs header */}
          <div
            style={{
              background: "linear-gradient(to right, #2C3E50, #4CA1AF)",
              padding: 15,
              borderRadius: 10,
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              marginBottom: 20,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              indicatorColor="primary"
              textColor="primary"
              centered
              sx={{
                "& .MuiTabs-flexContainer": { justifyContent: "center" },
                "& .MuiTab-root": { fontWeight: "bold", fontSize: "16px", color: "white", textTransform: "none" },
                "& .Mui-selected": { color: "#007BFF !important" },
              }}
            >
              <Tab label="📈 Progress Overview" />
              <Tab label="🔥 Reading Habits" />
              <Tab label="⏳ Reading/Comprehension Insights" />
              <Tab label="🎙️ Recordings" />
            </Tabs>
          </div>

          {/* Tab content */}
          <div style={{ marginTop: 20 }}>
            {activeTab === 0 && (
              <div className="chart-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <div
                  style={{
                    background: "rgba(255,255,255,0.9)",
                    padding: 20,
                    borderRadius: 10,
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                    textAlign: "center",
                    height: 300,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <h2 style={{ fontSize: 14, marginBottom: 5 }}>📈 Daily Stories Generated</h2>
                  <p style={{ color: "#555", fontSize: 12 }}>Last 7 days</p>
                  <div style={{ height: 220, position: "relative" }}>
                    <Bar data={storiesReadData} options={{ responsive: true, maintainAspectRatio: false }} />
                  </div>
                </div>

                <div
                  style={{
                    background: "rgba(255,255,255,0.9)",
                    padding: 20,
                    borderRadius: 10,
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                    textAlign: "center",
                    height: 300,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <h2 style={{ fontSize: 14, marginBottom: 5 }}>🔥 Total Reading Time</h2>
                  <p style={{ color: "#555", fontSize: 12 }}>Last 7 days</p>
                  <div style={{ height: 220, position: "relative" }}>
                    <Line data={timeSpentReadingData} options={{ responsive: true, maintainAspectRatio: false }} />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 1 && (
              <div className="chart-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <div
                  style={{
                    background: "rgba(255,255,255,0.9)",
                    padding: 20,
                    borderRadius: 10,
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                    textAlign: "center",
                    height: 300,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <h2 style={{ fontSize: 14, marginBottom: 5 }}>🔥 Weekly Reading Streak</h2>
                  <p style={{ color: "#555", fontSize: 12 }}>Last 6 weeks</p>
                  <div style={{ height: 220, position: "relative" }}>
                    <Bar
                      data={readingStreaksData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: { y: { beginAtZero: true, max: 7 }, x: { title: { display: true, text: "Week" } } },
                      }}
                    />
                  </div>
                </div>

                <div
                  style={{
                    background: "rgba(255,255,255,0.9)",
                    padding: 20,
                    borderRadius: 10,
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                    textAlign: "center",
                    height: 300,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <h2 style={{ fontSize: 14, marginBottom: 5 }}>🌟 Favorite Genres</h2>
                  <p style={{ color: "#555", fontSize: 12 }}>All-time counts</p>
                  <div style={{ height: 220, position: "relative" }}>
                    <Bar data={favoriteGenresData} options={{ responsive: true, maintainAspectRatio: false }} />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 2 && (
              <div className="chart-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <div
                  style={{
                    background: "rgba(255,255,255,0.9)",
                    padding: 20,
                    borderRadius: 10,
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                    textAlign: "center",
                    height: 300,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <h2 style={{ fontSize: 14, marginBottom: 5 }}>⏳ Average Reading Time</h2>
                  <p style={{ color: "#555", fontSize: 12 }}>Last 7 days</p>
                  <div style={{ height: 220, position: "relative" }}>
                    <Line data={averageReadingTimeData} options={{ responsive: true, maintainAspectRatio: false }} />
                  </div>
                </div>

                <div
                  style={{
                    background: "rgba(255,255,255,0.9)",
                    padding: 20,
                    borderRadius: 10,
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                    textAlign: "center",
                    height: 300,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <h2 style={{ fontSize: 14, marginBottom: 5 }}>🧠 Comprehension Scores</h2>
                  <p style={{ color: "#555", fontSize: 12 }}>All-time</p>
                  <div style={{ height: 220, position: "relative" }}>
                    <Line
                      data={comprehensionScoresData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: { title: { display: true, text: "Score (%)" }, beginAtZero: true, max: 100 },
                          x: { title: { display: true, text: "Date" } },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 3 && (
              <div
                style={{
                  background: "rgba(255,255,255,0.95)",
                  padding: 20,
                  borderRadius: 10,
                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                }}
              >
                <h2 style={{ fontFamily: "Montserrat, sans-serif", marginBottom: 10 }}>
                  🎧 Saved Read-Aloud Recordings
                </h2>

                {!userDoc?.recordings || userDoc.recordings.length === 0 ? (
                  <div style={{ textAlign: "center", marginTop: 30 }}>
                    <p style={{ color: "#777", marginBottom: 20 }}>
                      No recordings saved yet. Record yourself reading a story to see them here!
                    </p>
                  </div>
                ) : (
                  <>
                    {userDoc.recordings.map((rec, idx) => (
                      <div key={idx} style={{ marginBottom: 15, borderBottom: "1px solid #ccc", paddingBottom: 10 }}>
                        <p>
                          <strong>{rec.storyTitle || rec.fileName}</strong>
                        </p>
                        <p style={{ fontSize: "0.9em", color: "#555" }}>
                          Uploaded: {rec.uploadedAt ? new Date(rec.uploadedAt).toLocaleString() : "Unknown"}
                        </p>
                        <audio controls src={rec.url} style={{ width: "100%", marginTop: 8 }} />

                        <button
                          onClick={async () => {
                            if (!window.confirm("Delete this recording?")) return;
                            try {
                              const storage = getStorage();
                              if (rec.storagePath) {
                                await deleteObject(ref(storage, rec.storagePath));
                              }
                              const user = auth.currentUser;
                              if (!user) throw new Error("Not signed in");
                              const userRef = doc(db, "users", user.uid);
                              await updateDoc(userRef, { recordings: arrayRemove(rec) });
                            } catch (err) {
                              console.error("Delete recording error:", err);
                              alert("Failed to delete recording.");
                            }
                          }}
                          style={{
                            marginTop: 10,
                            backgroundColor: "#ff4d4d",
                            color: "white",
                            padding: "6px 12px",
                            borderRadius: 5,
                            border: "none",
                            cursor: "pointer",
                            marginRight: 10,
                          }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="page-footer">
        <p>&copy; {new Date().getFullYear()} YourStoryWorld. All rights reserved.</p>
        <nav style={{ marginTop: 10 }}>
          <a href="/about" className="footer-link">About Us</a>
          <a href="/contact" className="footer-link">Contact</a>
        </nav>
      </footer>
    </div>
  );
};

export default PersonalDashboard;

